import { Schema } from "effect"
import { Actor, AgentKind, Effort, Evidence, Hold, Principal, RecordMeta, WorkRef } from "../common"
import {
  AgentId,
  AreaId,
  ArtifactId,
  CommentId,
  EdgeId,
  GoalId,
  HookId,
  IntegrationId,
  IntentKey,
  ItemId,
  KpiId,
  Markdown,
  MemoryItemId,
  Millis,
  NonNegInt,
  PosInt,
  ProfileId,
  ProposalId,
  RunId,
  RunnerId,
  Seq,
  Slug,
  TypeKey,
  UserId,
  WorkRequestId,
} from "../ids"
import { Placement } from "../placement"
import { ItemRouting } from "../routing"
import { BetVerdictValue, Horizon, MetricCondition, NodeBase } from "./plan"

// #region checks-and-review
export const ArtifactKind = Schema.Literals([
  "pull_request",
  "branch",
  "commit",
  "file",
  "document",
  "page_draft",
  "image",
  "report",
  "dataset",
  "build",
  "link",
  "log",
  // company-wide content (decision 0003): editorial and social work products
  "article",
  "video",
  "script",
  "brief",
])

/**
 * A check that decides "done" (the item's "Done when"). Written before work starts, frozen when the first attempt
 * starts, read-only to workers. Checks are instructions and evidence requirements, never shell verbs in a job: a
 * `command` check is run by the worker and re-run by the validator inside their own CLI sessions.
 */
export const DoneCheck = Schema.TaggedUnion({
  command: { name: Schema.String, description: Schema.String }, // "pnpm test", "lighthouse ≥ 90"
  artifact: { kind: ArtifactKind, description: Schema.String }, // an artifact of this kind must be posted
  rubric: { criteria: Schema.NonEmptyArray(Schema.String) }, // judged by a validator, cross-model by default
  human: { approverId: Schema.NullOr(UserId), prompt: Schema.String }, // null → the accountable person
  metric: { condition: MetricCondition, afterDays: Schema.Int }, // outcome check (Phase 3); never gates `done`
})
export type DoneCheck = typeof DoneCheck.Type

/** Verification ladder stages, in order. `agent` = the owner agent reviews; `validator` = a separate run;
 * `human` = a question proposal to `escalateTo` (or the check's approver, or the accountable person). */
export const ReviewStage = Schema.Literals(["agent", "validator", "human"])
export const ReviewConfig = Schema.Struct({
  stages: Schema.NonEmptyArray(ReviewStage), // default ["agent"]; W15 adds "validator", "human" in repo projects
  crossModel: Schema.Boolean, // validator runs on the other model family (default true)
  maxRounds: Schema.Int, // 2–3; then escalate to a person
  /** null = the item's accountable person (core `accountablePerson`), resolved when someone is needed. */
  escalateTo: Schema.NullOr(UserId),
})
export type ReviewConfig = typeof ReviewConfig.Type
export const Review = Schema.Struct({
  ...ReviewConfig.fields,
  round: Schema.Int, // 0 before the first review
  stage: Schema.Int, // index into stages for the current round
  /** Decision 0050: rounds a person sent back at the human stage; not counted toward maxRounds. Absent = 0. */
  personRounds: Schema.optionalKey(NonNegInt),
})
export type Review = typeof Review.Type

/** What a worker or agent reports as the obstacle. */
export const Blocker = Schema.TaggedUnion({
  needs_input: { from: Schema.Literals(["assignee", "lead", "human"]), what: Schema.String },
  missing_access: { what: Schema.String }, // integration grant, repo, secret
  environment_broken: { what: Schema.String }, // build env, simulator, CI
  depends_on: { itemId: ItemId }, // the owner should add a blocks edge
  other: { what: Schema.String },
})
export type Blocker = typeof Blocker.Type

export const ImpossibleReason = Schema.Literals([
  "spec_conflict",
  "contract_unreachable",
  "premise_false",
  "out_of_scope",
])

/** Why an item is `blocked`. Every human-needing block has exactly one escalation in the ledger. */
export const BlockReason = Schema.TaggedUnion({
  needs_human: { proposalId: ProposalId }, // non-retryable stop, repeated failure, review rounds exhausted, …
  question: { askId: CommentId }, // waiting for an agent's answer (any agent, `to`; else one level up)
  reported: { blocker: Blocker, runId: Schema.NullOr(RunId) }, // the owner resolves it
  impossible_claimed: { runId: RunId, reason: ImpossibleReason }, // the owner accepts it or revises the checks
})
export type BlockReason = typeof BlockReason.Type

/** Worker defaults: which CLI family, model and effort. Agents set defaults; items override. */
export const Hands = Schema.Struct({
  agent: Schema.NullOr(AgentKind),
  model: Schema.NullOr(Schema.String),
  effort: Schema.NullOr(Effort),
})
export type Hands = typeof Hands.Type
// #endregion checks-and-review

// #region item
/**
 * One status set for every item (decision 0035). Top-level plannable items and top-level containers show
 * backlog / todo / in_progress as Later / Next / Now; every other item as Backlog / Todo / In progress.
 * Readiness is DERIVED (core `readiness`), never stored.
 */
export const ItemStatus = Schema.Literals([
  "triage", // captured, not accepted
  "backlog", // accepted, not planned (Later); parks everything below it
  "todo", // planned (Next); a leaf runs once ready
  "in_progress", // Now (containers: children unlocked); a leaf with a live run; a person's leaf they started
  "in_review", // done claimed, review stages running
  "blocked", // explicit, with a BlockReason; never retried on its own
  "done", // through its review stages (invariant 6)
  "cancelled", // closed without doing it; carries `resolution`
])
export type ItemStatus = typeof ItemStatus.Type

/** Why an item was cancelled. Set iff status = cancelled. */
export const Resolution = Schema.TaggedUnion({
  wont_do: {},
  duplicate: { of: ItemId },
  impossible: { reason: Schema.String }, // "" = no reason recorded (old data)
  superseded: { by: ItemId },
  /** Decision 0101: it can't be done here and nobody here can grant what it needs (`what`: the external blocker). */
  blocked_external: { what: Schema.String },
})
export type Resolution = typeof Resolution.Type

/**
 * Decision 0101: what a `done` item delivered, so a done item says honestly what it is. `verified`: the result was
 * checked (the default when absent); `report_delivered`: the deliverable is a report or finding; `no_data`: the work
 * found nothing to deliver (the data does not exist or is empty) and says so.
 */
export const CloseResult = Schema.Literals(["verified", "report_delivered", "no_data"])
export type CloseResult = typeof CloseResult.Type

/**
 * Who does an item (direction notes, note 7). The OWNER answers for it (`Item.owner`); the ASSIGNEE does it.
 * `worker`: "a new worker" for the owner agent, which supervises it (needs an agent owner); null preset = the project's
 * default. `agent`: the owner agent itself, in its own session: RESERVED (decide refuses it this release).
 */
export const Assignee = Schema.TaggedUnion({
  user: { userId: UserId },
  worker: { preset: Schema.NullOr(Slug) },
  agent: { agentId: AgentId },
})
export type Assignee = typeof Assignee.Type

/** Where a triage item was captured (was IntakeSource; same members). Read-only. */
export const CaptureSource = Schema.TaggedUnion({
  web: {},
  cli: {},
  mcp: { client: Schema.String }, // "claude-code", "codex", "claude.ai", "chatgpt"
  email: { from: Schema.String },
  webhook: { hookId: HookId },
  agent: {}, // an agent's turn; createdBy names the agent and its run
})
export type CaptureSource = typeof CaptureSource.Type

/**
 * A recurring item's schedule (decision 0035). The clock is OFF this release: nothing creates occurrences on a
 * schedule; a person's Run now (RunOccurrence) creates one. Other triggers and an `enabled` switch come later as
 * additive optional keys.
 */
export const Recurrence = Schema.Struct({
  /** 5-field cron and an IANA zone ("0 9 * * MON", "Europe/Berlin"); checked by decide. Never fires this release. */
  schedule: Schema.Struct({ cron: Schema.String, tz: Schema.String }),
  /** An occurrence is due this long after it is created (null = no due date). */
  window: Schema.NullOr(Millis),
  /** The occurrence title: `{title}` = the series' title, `{period}` = the date in the schedule's zone. */
  title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
})
export type Recurrence = typeof Recurrence.Type

/** Exploratory work (an optional section of a top-level item). Evaluation behaviour (kill criteria, verdicts) is Phase 3. */
export const Bet = Schema.Struct({
  hypothesis: Schema.String,
  inputKpiId: KpiId, // required
  laggingKpiId: Schema.NullOr(KpiId),
  success: Schema.Struct({ condition: MetricCondition, evalAfterDays: Schema.Int }),
  kill: Schema.Array(Schema.Struct({ condition: MetricCondition, byDays: Schema.Int })),
  /** Shape Up appetite = circuit breaker. `runs` bounds worker attempts across the bet. */
  appetite: Schema.Struct({ calendarDays: Schema.Int, runs: Schema.Int }),
  alternativesConsidered: Schema.Array(ItemId),
  /** Set only by an evaluator agent, never by a worker or the bet's own owner agent. */
  verdict: Schema.NullOr(
    Schema.Struct({ value: BetVerdictValue, by: AgentId, at: Millis, evidence: Schema.Array(Evidence) }),
  ),
  /** A killed bet can't be retried without evidence contradicting this learning. */
  learningId: Schema.NullOr(MemoryItemId),
})
export type Bet = typeof Bet.Type

/** Cross-area request: the receiving area's agent accepts or declines (a graph edit, not chat). */
export const AreaRequest = Schema.Struct({
  fromAreaId: AreaId,
  state: Schema.Literals(["pending", "accepted", "declined"]),
})

/** The description's revision ("rev 3 · Jannik · 2 h ago"). Bumped by a v2 ItemUpdated that changes `body`. */
export const SpecRev = Schema.Struct({ n: PosInt, at: Millis, by: Actor })
export type SpecRev = typeof SpecRev.Type

/** An initiative's v1 lifecycle (decision 0036): only on items decoded from v1 records. */
export const V1InitiativeStatus = Schema.Literals([
  "shaping",
  "active",
  "paused",
  "done",
  "killed",
  "abandoned",
])
export type V1InitiativeStatus = typeof V1InitiativeStatus.Type

/** v1 facts with no v2 field, kept on items decoded from v1 records. Never written by decide. */
export const LegacyItem = Schema.Struct({
  initiative: Schema.optionalKey(
    Schema.Struct({
      status: V1InitiativeStatus,
      horizon: Horizon,
      definitionOfDone: Schema.NullOr(Schema.NonEmptyArray(Schema.String)),
      supportsKpiId: Schema.NullOr(KpiId),
    }),
  ),
  intake: Schema.optionalKey(
    Schema.Struct({
      kind: Schema.Literals(["idea", "feature", "bug", "question"]),
      attachments: Schema.Array(ArtifactId),
    }),
  ),
})
export type LegacyItem = typeof LegacyItem.Type

/**
 * Anything that can be finished (decision 0035): one noun, one tree of at most 4 levels (top-level = depth 0). Replaces
 * Initiative, Task and Intake. A container is an item with at least one child that is not cancelled; its owner closes it.
 */
export const Item = Schema.Struct({
  ...NodeBase, // RecordMeta, ref, title, why (one line), body (THE spec), rank, hold, tags (labels)
  id: ItemId,
  /** A key of the type registry (`ITEM_TYPES`): a label with defaults; never adds fields. */
  type: TypeKey,
  /** `publish` is the outward step: the only kind that may hold outward_action executions ("Publish" badge). */
  kind: Schema.Literals(["work", "publish"]),
  parentId: Schema.NullOr(ItemId),
  /** Top-level items only ([] below; children inherit through the top-level item). */
  goalIds: Schema.Array(GoalId),
  /** The lane. Required from `todo` on (decide); defaults to the parent's. */
  areaId: Schema.NullOr(AreaId),
  /** Answers for it: approvals, human checks, escalations, a worker's questions. Required from `todo` on (decide). */
  owner: Schema.NullOr(Principal),
  /** Does it (leaves). null on plannable items (their children do the work) and in triage/backlog without an owner. */
  assignee: Schema.NullOr(Assignee),
  status: ItemStatus,
  resolution: Schema.NullOr(Resolution),
  /** Soft target; never gates. "Overdue" after it. */
  dueAt: Schema.NullOr(Millis),
  /** Gate: not ready before. */
  notBefore: Schema.NullOr(Millis),
  recurrence: Schema.NullOr(Recurrence),
  /** "Done when": needed before a worker starts a leaf (was Task.contract.checks). */
  checks: Schema.Array(DoneCheck),
  /** Set when the first attempt starts; later changes need a person (was Task.contract.frozenAt). */
  checksFrozenAt: Schema.NullOr(Millis),
  /**
   * Decision 0116: who wrote the current checks (a person also when they approved an agent's card for them; null =
   * Minga or a worker). Frozen checks stay a person's decision only when a person wrote them. Absent = from before
   * 0116: the item's creator.
   */
  checksBy: Schema.optionalKey(Schema.NullOr(Principal)),
  /** null = planned directly. Read-only. */
  source: Schema.NullOr(CaptureSource),
  /** Captured from outside: rendered and packed as quoted data. */
  tainted: Schema.Boolean,
  bet: Schema.NullOr(Bet),
  request: Schema.NullOr(AreaRequest),
  // ---- execution (meaning unchanged from Task) ----
  blocked: Schema.NullOr(BlockReason), // non-null iff status = blocked
  paused: Schema.Boolean, // a hold: the scheduler skips it
  /** Who paused it (decision 0032): a person's pause is never resumed by an agent. null = the system. */
  pausedBy: Schema.optionalKey(Schema.NullOr(Principal)),
  pauseNote: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /** The evidence of a CloseItem{done}, kept while the item passes its later review stages. */
  closeEvidence: Schema.optionalKey(Schema.Array(Evidence)),
  /** Meaning-based dedupe: one open item per (parentId, intentKey, owner). */
  intentKey: IntentKey,
  review: Review,
  placement: Placement, // item overrides; merged over area defaults when a run is created, then frozen on the run
  hands: Hands,
  routing: Schema.optionalKey(Schema.NullOr(ItemRouting)),
  /**
   * Decision 0101: the item changes no repo (research, data, analysis): no pull request check, no draft PR, and its
   * validator reviews a checkout of the base branch. Absent = false.
   */
  readOnly: Schema.optionalKey(Schema.Boolean),
  /** Decision 0101: what a close as done said it delivered (`CloseItem.result`). Absent = not said. */
  result: Schema.optionalKey(CloseResult),
  paths: Schema.Array(Schema.String), // memory triggers ("/compare/**")
  integrations: Schema.Array(IntegrationId), // needed grants; a subset of the area's grants
  attempts: Schema.Int, // worker attempts started
  /**
   * Worker attempts without measured progress since a person last acted. Used for delegated recovery and optional
   * project worker budgets. Absent = 0 (records from before the sim fixes wave).
   */
  unattendedAttempts: Schema.optionalKey(NonNegInt),
  /**
   * Every attempt since a person last acted, progressed or not; optional project worker budgets use this count. Derived by
   * evolve from v2 events only. Absent = 0.
   */
  autoAttempts: Schema.optionalKey(NonNegInt),
  /** Set by a retry decision: the next attempt waits until `at`; rate_limited pins the same profile. */
  retry: Schema.NullOr(Schema.Struct({ at: Millis, pinProfile: Schema.NullOr(ProfileId) })),
  activeRunId: Schema.NullOr(RunId),
  lastRunId: Schema.NullOr(RunId),
  supersedes: Schema.NullOr(ItemId),
  // ---- bookkeeping (written by v2 events only; decision 0036) ----
  specRev: SpecRev,
  startedAt: Schema.NullOr(Millis), // first move to in_progress
  closedAt: Schema.NullOr(Millis), // done or cancelled
  overdueNoticed: Schema.NullOr(Millis), // the dueAt an ItemOverdue was recorded for
  /** Old intake only: the seq of its capture (Redacted matching). */
  contentSeq: Schema.optionalKey(Seq),
  legacy: Schema.optionalKey(LegacyItem),
  /**
   * Wave F (decision 0062): who asked, stamped by decide at creation (a person; the asker of the turn that created it;
   * the asker of the worker run that created it). Its workers' runs and the follow-up turns inherit it. Absent before.
   */
  askedBy: Schema.optionalKey(Principal),
  /** Scoped provenance for delegated work; descendants inherit this association when created. */
  workRequest: Schema.optionalKey(Schema.Struct({ requestId: WorkRequestId, revision: PosInt })),
  /**
   * Wave E (decision 0063): another person's personal runner its owner lent to this item (`LendRunner`). Covers the
   * item's workers, validators and retries; any shaping command ends it (`null`). Written by decide only. Absent = none.
   */
  lentRunnerId: Schema.optionalKey(Schema.NullOr(RunnerId)),
})
export type Item = typeof Item.Type

/** UpdateItem.patch and ItemUpdated.patch/before: every key optional (absent = unchanged). */
export const ItemPatch = Schema.Struct({
  title: Schema.optionalKey(Schema.String),
  why: Schema.optionalKey(Schema.String),
  body: Schema.optionalKey(Markdown),
  type: Schema.optionalKey(TypeKey),
  kind: Schema.optionalKey(Schema.Literals(["work", "publish"])),
  tags: Schema.optionalKey(Schema.Array(Slug)),
  paths: Schema.optionalKey(Schema.Array(Schema.String)),
  integrations: Schema.optionalKey(Schema.Array(IntegrationId)),
  placement: Schema.optionalKey(Placement),
  hands: Schema.optionalKey(Hands),
  routing: Schema.optionalKey(Schema.NullOr(ItemRouting)),
  /** Decision 0101: the item changes no repo. */
  readOnly: Schema.optionalKey(Schema.Boolean),
  /** Top-level items only. */
  goalIds: Schema.optionalKey(Schema.Array(GoalId)),
  /** Only before the first attempt. */
  areaId: Schema.optionalKey(Schema.NullOr(AreaId)),
  dueAt: Schema.optionalKey(Schema.NullOr(Millis)),
  notBefore: Schema.optionalKey(Schema.NullOr(Millis)),
  /** Capability schedule_work. */
  recurrence: Schema.optionalKey(Schema.NullOr(Recurrence)),
  bet: Schema.optionalKey(Schema.NullOr(Bet)),
  /** Worker-preset changes only (was UpdateTask.patch.executor); owner and other assignees use AssignItem. */
  assignee: Schema.optionalKey(Schema.NullOr(Assignee)),
  /** People only, and only `false` ("Adopt as description"); `before` may hold `true`. */
  tainted: Schema.optionalKey(Schema.Boolean),
  /**
   * Wave E (decision 0063): set by decide only, when a command shapes the item (`shapeAsker`). A client command whose
   * patch carries it is `Invalid("set by Minga")`.
   */
  askedBy: Schema.optionalKey(Principal),
  /** Wave E: set by decide only (`LendRunner`, and `null` by shaping). A client patch carrying it is refused. */
  lentRunnerId: Schema.optionalKey(Schema.NullOr(RunnerId)),
})
export type ItemPatch = typeof ItemPatch.Type
// #endregion item

// #region legacy-initiative
/** A v1 workstream shape, ids as ItemId (decision 0036: only in LegacyInitiativeUpdated). */
export const Workstream = Schema.Struct({
  definitionOfDone: Schema.NonEmptyArray(Schema.String),
  targetDate: Schema.NullOr(Millis),
  supportsKpiId: Schema.NullOr(KpiId),
})
export const LegacyInitiativeShape = Schema.TaggedUnion({ bet: Bet.fields, workstream: Workstream.fields })
export type LegacyInitiativeShape = typeof LegacyInitiativeShape.Type
/** The v1 InitiativePatch with ids as ItemId: the payload of LegacyInitiativeUpdated (upcast only, D9). */
export const LegacyInitiativePatch = Schema.Struct({
  title: Schema.optionalKey(Schema.String),
  why: Schema.optionalKey(Schema.String),
  body: Schema.optionalKey(Markdown),
  horizon: Schema.optionalKey(Horizon),
  goalIds: Schema.optionalKey(Schema.NonEmptyArray(GoalId)),
  ownerAgentId: Schema.optionalKey(Schema.NullOr(AgentId)),
  shape: Schema.optionalKey(LegacyInitiativeShape),
  tags: Schema.optionalKey(Schema.Array(Slug)),
})
export type LegacyInitiativePatch = typeof LegacyInitiativePatch.Type
// #endregion legacy-initiative

// #region edge
/**
 * Three edge kinds. `blocks`: `to` is not ready until `from` is done (drives the ready frontier); a `blocks` edge into
 * an item gates all of its descendants. `informs`: `to`'s context gets `from`'s outputs by reference (and its people's
 * notes); an input into a container reaches its descendants. `spawned_from`: provenance only; it never gates.
 * Parent/child is a field, KPI roll-up is a field, "worked on by" is derived from runs, human gates are
 * `proposal.blocks`.
 */
export const EdgeKind = Schema.Literals(["blocks", "informs", "spawned_from"])
export const Edge = Schema.Struct({
  ...RecordMeta,
  id: EdgeId,
  kind: EdgeKind,
  from: WorkRef,
  to: WorkRef,
  interfaceKey: Schema.NullOr(Slug), // informs: which published area interface flows along it
  note: Schema.NullOr(Schema.String),
  hold: Hold, // a person can lock a blocks edge so agents can't cut it
})
export type Edge = typeof Edge.Type
// #endregion edge
